import React from 'react';
import { useTheme } from '../../core/context/ThemeContext';
import { surfaces, textColors } from '../tokens';

/**
 * ThemeToggle Component
 * 
 * Button to switch between light and dark themes.
 * Shows sun icon in dark mode, moon icon in light mode.
 * Uses design system tokens for consistent styling.
 * 
 * @param {string} className - Additional CSS classes
 * @param {string} moonColor - Tailwind color class for moon icon (light mode), defaults to 'text-blue-600 dark:text-blue-400'
 * @param {string} sunColor - Tailwind color class for sun icon (dark mode), defaults to textColors.secondary
 * @param {string} moonHoverFill - Tailwind fill color class for moon hover effect (e.g., 'fill-blue-600', 'fill-white')
 */
export const ThemeToggle = ({ 
  className = '', 
  iconClassName = 'w-5 h-5',
  moonColor = 'text-blue-600 dark:text-blue-400',
  sunColor = textColors.secondary,
  moonHoverFill = 'hover:fill-blue-600 dark:hover:fill-blue-400'
}) => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-lg transition-colors ${surfaces.hover} group ${className}`}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {isDark ? (
        // Sun icon for dark mode
        <svg className={`${iconClassName} ${sunColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        // Moon icon for light mode
        <svg className={`${iconClassName} ${moonColor} fill-none ${moonHoverFill} transition-all`} stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
};

export default ThemeToggle;

